import React from 'react';
import { Modal, Table, Tag, Pagination } from 'antd';
import { getChunkList, KnowledgeChunkVo } from '@/api/knowledgeBase';
import styles from './chunkModal.module.scss';

interface Props {
  visible: boolean;
  itemId: number | null;
  itemName: string;
  onClose: () => void;
}

const ChunkModal: React.FC<Props> = ({ visible, itemId, itemName, onClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [chunks, setChunks] = React.useState<KnowledgeChunkVo[]>([]);
  const [total, setTotal] = React.useState(0);
  const [pageNum, setPageNum] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);

  React.useEffect(() => {
    if (visible && itemId) {
      loadChunks(1, pageSize);
    }
  }, [visible, itemId]);

  const loadChunks = (page: number, size: number) => {
    if (!itemId) return;
    setLoading(true);
    getChunkList({ itemId, pageNum: page, pageSize: size })
      .then((res) => {
        setChunks(res.records || []);
        setTotal(res.total || 0);
        setPageNum(res.current || page);
        setPageSize(res.size || size);
      })
      .catch((err) => {
        console.error('获取chunk列表失败:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePageChange = (page: number, size: number) => {
    loadChunks(page, size);
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'chunkIndex',
      key: 'chunkIndex',
      width: 60,
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      render: (text: string) => (
        <div className={styles.contentCell}>
          {text.length > 200 ? text.slice(0, 200) + '...' : text}
        </div>
      ),
    },
    {
      title: 'Embedding状态',
      dataIndex: 'embeddingStatus',
      key: 'embeddingStatus',
      width: 100,
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '已生成' : '失败'}
        </Tag>
      ),
    },
    {
      title: '向量维度',
      dataIndex: 'embeddingDimensions',
      key: 'embeddingDimensions',
      width: 80,
      render: (dim: number) => dim || '-',
    },
    {
      title: '模型',
      dataIndex: 'embeddingModel',
      key: 'embeddingModel',
      width: 120,
      ellipsis: true,
    },
  ];

  return (
    <Modal
      title={`条目分块详情 - ${itemName}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      className={styles.chunkModal}
    >
      <Table<KnowledgeChunkVo>
        columns={columns}
        dataSource={chunks}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
      />
      <div className={styles.paginationWrapper}>
        <Pagination
          current={pageNum}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          showTotal={(t) => `共 ${t} 条`}
          onChange={(page, newPageSize) => {
            const actualPageSize = newPageSize || pageSize;
            loadChunks(page, actualPageSize);
          }}
          pageSizeOptions={['10', '20', '50', '100']}
        />
      </div>
    </Modal>
  );
};

export default ChunkModal;
